<script>
export default {
  props: {
    customer: {
      type: Object,
      required: true,
    },
    api: {
      type: Function,
      required: true,
    },
    apiError: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      editedCustomer: {
        firstname: this.customer.firstname,
        lastname: this.customer.lastname,
        email: this.customer.email,
        phone: this.customer.phone,
        birthday: this.customer.birthday && new Date(this.customer.birthday),
      },
      customAnswers: {},
    };
  },
  computed: {
    authStore() {
      return this.$store.getters['auth/store'];
    },
    customFields() {
      const opts = this.authStore && this.authStore.custom_fields_options;
      if (!opts || !opts.enabled) return [];
      return opts.fields || [];
    },
  },
  created() {
    this.customAnswers = this.buildInitialAnswers();
  },
  methods: {
    buildInitialAnswers() {
      const existing = ((this.customer && this.customer.custom_fields_data) || [])
        .reduce((acc, a) => {
          acc[a.name] = a.value;
          return acc;
        }, {});

      return this.customFields.reduce((acc, field) => {
        if (Object.prototype.hasOwnProperty.call(existing, field.name)) {
          acc[field.name] = field.type === 'checkbox'
            ? existing[field.name] === 'true' || existing[field.name] === true
            : existing[field.name];
        } else {
          acc[field.name] = field.type === 'checkbox' ? false : '';
        }
        return acc;
      }, {});
    },
    formatAnswer(field, raw) {
      if (field.type === 'checkbox') {
        return { value: raw ? 'true' : 'false', label: raw ? 'Oui' : 'Non' };
      }
      if (field.type === 'select') {
        return { value: raw, label: String(raw ?? '').slice(0, 255) };
      }
      return { value: String(raw ?? ''), label: String(raw ?? '').slice(0, 255) };
    },
    isAnswerEmpty(field, raw) {
      if (field.type === 'checkbox') return false;
      return raw === '' || raw === null || raw === undefined;
    },
    buildCustomFieldsPayload() {
      const payload = [];

      for (const field of this.customFields) {
        const raw = this.customAnswers[field.name];
        if (field.required && this.isAnswerEmpty(field, raw)) {
          this.$showMessage.error(`Le champ "${field.label}" est obligatoire.`);
          return null;
        }
        if (!this.isAnswerEmpty(field, raw)) {
          payload.push({
            name: field.name,
            answer: this.formatAnswer(field, raw),
          });
        }
      }

      return payload;
    },
    handle() {
      const data = { ...this.editedCustomer };

      if (data.birthday) {
        data.birthday = this.$moment(data.birthday).format('YYYY-MM-DD');
      }

      if (this.customFields.length) {
        const customFieldsData = this.buildCustomFieldsPayload();
        if (customFieldsData === null) return;
        data.custom_fields_data = customFieldsData;
      }

      this.isLoading = true;
      this.api(data)
        .then(() => this.$emit('done'))
        .catch(this.apiError || ((error) => this.$errorHandlers.axios(error)))
        .finally(() => (this.isLoading = false));
    },
  },
};
</script>

<template>
  <AppCardModal
    tag="form"
    @submit.prevent="dataIsValid(isLoading) && handle()"
  >
    <template #title>
      Modifier les informations
    </template>
    <template #body>
      <div class="columns is-multiline">
        <div class="column is-6">
          <b-field>
            <template #label>
              Prénom
              <span class="has-text-danger">*</span>
            </template>
            <b-input
              v-model="editedCustomer.firstname"
              maxlength="128"
              :has-counter="false"
              required
            />
          </b-field>
        </div>
        <div class="column is-6">
          <b-field>
            <template #label>
              Nom
              <span class="has-text-danger">*</span>
            </template>
            <b-input
              v-model="editedCustomer.lastname"
              maxlength="128"
              :has-counter="false"
            />
          </b-field>
        </div>
        <div class="column is-6">
          <b-field>
            <template #label>
              Adresse e-mail
              <span class="has-text-danger">*</span>
            </template>
            <b-input v-model="editedCustomer.email" type="email" required />
          </b-field>
        </div>
        <div class="column is-6">
          <b-field label="Téléphone">
            <b-input v-model="editedCustomer.phone" type="tel" />
          </b-field>
        </div>

        <template v-if="customFields.length">
          <div class="column is-12">
            <hr class="my-0">
            <p class="has-text-weight-medium mt-4 mb-2">
              Questions personnalisées
            </p>
          </div>
          <div
            v-for="field in customFields"
            :key="field.name"
            class="column is-12"
          >
            <b-field>
              <template v-if="field.type !== 'checkbox'" #label>
                {{ field.label }}
                <span v-if="field.required" class="has-text-danger">*</span>
              </template>

              <b-input
                v-if="field.type === 'text'"
                v-model="customAnswers[field.name]"
                maxlength="100"
                :has-counter="false"
                :required="field.required"
                expanded
              />
              <b-input
                v-else-if="field.type === 'textarea'"
                v-model="customAnswers[field.name]"
                type="textarea"
                maxlength="1000"
                :required="field.required"
                expanded
              />
              <b-select
                v-else-if="field.type === 'select'"
                v-model="customAnswers[field.name]"
                :required="field.required"
                expanded
              >
                <option value="" disabled>
                  -- Sélectionner --
                </option>
                <option v-for="opt in field.options || []" :key="opt" :value="opt">
                  {{ opt }}
                </option>
              </b-select>
              <b-checkbox
                v-else-if="field.type === 'checkbox'"
                v-model="customAnswers[field.name]"
              >
                {{ field.label }}
                <span v-if="field.required" class="has-text-danger">*</span>
              </b-checkbox>
            </b-field>
          </div>
        </template>
      </div>
    </template>
    <template #foot>
      <b-button
        type="is-primary"
        native-type="submit"
        :loading="isLoading"
      >
        Modifier
      </b-button>
    </template>
  </AppCardModal>
</template>
